import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

/**
 * "A new version is available" (deploy detection).
 *
 * Every build stamps the same `__BUILD_ID__` into the SPA and the Worker
 * (vite `define`, contracts/build-id.d.ts). The Worker reports its id on
 * `/health` and as the first event of the notification stream; whenever the
 * reported id differs from the one this bundle was built with, a deploy has
 * happened since the page loaded.
 *
 * Detection: the stream's `version` event (fires on every reconnect, so also
 * after a deploy drops the old connection), plus a `/health` check when the
 * tab becomes visible or focused and on a slow interval — a Worker keeps
 * serving in-flight streams from the old version for a while, so a fresh
 * request is the reliable signal.
 *
 * Reaction: a persistent toast with a reload button. A tab nobody is using
 * (no input for IDLE_RELOAD_MS, or hidden) is reloaded automatically —
 * editor changes are acked by the store and chat drafts live in
 * localStorage, so nothing is lost.
 */

export const CURRENT_BUILD_ID: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'
/** Release version of this bundle ("v1.2.3", "v1.2.3-dev.<sha>" or "dev") — see scripts/build-version.mjs. */
export const CURRENT_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev'

/** No pointer/keyboard input for this long → the tab may reload itself. */
export const IDLE_RELOAD_MS = 5 * 60 * 1000
/** Hidden tabs reload sooner: the user is not looking at it. */
export const HIDDEN_RELOAD_MS = 60 * 1000
const HEALTH_CHECK_INTERVAL_MS = 10 * 60 * 1000
const ACTIVITY_THROTTLE_MS = 1_000
const TOAST_ID = 'app-new-version'

type Listener = (buildId: string) => void

let serverBuildId: string | null = null
/** Dev helper: pretend a deploy happened (see `window.hub.simulateNewVersion()`). */
let simulated = false
const listeners = new Set<Listener>()

/** Called with every build id the server reports (SSE event, health check). */
export function reportServerBuildId(buildId: string): void {
  if (!buildId || buildId === serverBuildId) return
  serverBuildId = buildId
  for (const listener of listeners) listener(buildId)
}

export function isNewVersionAvailable(): boolean {
  if (simulated) return true
  return (
    serverBuildId !== null &&
    serverBuildId !== CURRENT_BUILD_ID &&
    CURRENT_BUILD_ID !== 'dev' &&
    serverBuildId !== 'dev'
  )
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  // In the browser console: `hub.simulateNewVersion()` shows the toast and
  // arms the idle reload exactly as a real deploy would.
  ;(window as unknown as { hub?: Record<string, unknown> }).hub = {
    ...(window as unknown as { hub?: Record<string, unknown> }).hub,
    simulateNewVersion: () => {
      simulated = true
      for (const listener of listeners) listener('simulated')
    },
  }
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export async function checkServerBuildId(): Promise<void> {
  try {
    const res = await fetch('/health', { cache: 'no-store', credentials: 'same-origin' })
    if (!res.ok) return
    const data = (await res.json()) as { buildId?: unknown }
    if (typeof data.buildId === 'string') reportServerBuildId(data.buildId)
  } catch {
    // offline / transient — the next trigger tries again
  }
}

/**
 * Pure decision: reload a tab that nobody is using. Exported for tests.
 */
export function shouldAutoReload(input: {
  newVersion: boolean
  hidden: boolean
  lastActivityAt: number
  hiddenSince: number | null
  now: number
}): boolean {
  if (!input.newVersion) return false
  if (input.hidden && input.hiddenSince !== null) {
    return input.now - input.hiddenSince >= HIDDEN_RELOAD_MS
  }
  return input.now - input.lastActivityAt >= IDLE_RELOAD_MS
}

function reload(): void {
  window.location.reload()
}

/**
 * Mount once in the root layout. Shows the toast and schedules the idle
 * reload; renders nothing.
 */
export function AppVersionWatcher() {
  const { t } = useTranslation('common')

  useEffect(() => {
    // Under `vite dev` both sides report "dev", so nothing ever fires here
    // unless `hub.simulateNewVersion()` is called from the console.
    let lastActivityAt = Date.now()
    let lastActivityMark = 0
    let hiddenSince: number | null = document.hidden ? Date.now() : null
    let announced = false
    let stopped = false

    const evaluate = () => {
      if (stopped || !isNewVersionAvailable()) return
      const now = Date.now()
      if (
        shouldAutoReload({
          newVersion: true,
          hidden: document.hidden,
          lastActivityAt,
          hiddenSince,
          now,
        })
      ) {
        stopped = true
        reload()
        return
      }
      if (!announced) {
        announced = true
        toast.info(t('newVersion.title'), {
          id: TOAST_ID,
          description: t('newVersion.description'),
          duration: Infinity,
          action: { label: t('newVersion.reload'), onClick: reload },
        })
      }
    }

    const onActivity = () => {
      const now = Date.now()
      if (now - lastActivityMark < ACTIVITY_THROTTLE_MS) return
      lastActivityMark = now
      lastActivityAt = now
    }
    const onVisibility = () => {
      if (document.hidden) {
        hiddenSince = Date.now()
      } else {
        hiddenSince = null
        // Coming back counts as activity: give the user the toast rather
        // than yanking the page away the moment they look at it.
        lastActivityAt = Date.now()
        void checkServerBuildId().then(evaluate)
      }
    }
    const onFocus = () => void checkServerBuildId().then(evaluate)

    const activityEvents = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const
    for (const name of activityEvents) window.addEventListener(name, onActivity, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    const unsubscribe = subscribe(evaluate)
    const healthTimer = setInterval(() => void checkServerBuildId(), HEALTH_CHECK_INTERVAL_MS)
    const idleTimer = setInterval(evaluate, 30_000)

    void checkServerBuildId().then(evaluate)

    return () => {
      stopped = true
      for (const name of activityEvents) window.removeEventListener(name, onActivity)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      unsubscribe()
      clearInterval(healthTimer)
      clearInterval(idleTimer)
    }
  }, [t])

  return null
}
